import React from 'react';

import { ChapterWrapper } from '~/ChapterWrapper';
import { ELink } from '~/ExternalLink';
import Footer from '~/Footer';
import { Path } from '~/paths';

// const nv_3models_intro = require('../assets/images/nv_3models_intro.png');


export const StudentDistributionMeta = {
    title: "Studentovo rozdělení *",
    shortTitle: "Studentovo rozdělení *",
    path: Path.student_distribution,
    element: (sectionNumber:string) => <StudentDistribution sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec: string[]  = [
    "stud-formula",
    "stud-motivation",
    "stud-derivation",
];


export function StudentDistribution({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={StudentDistributionMeta.title} TOCSpec={TOCSpec}>

            <i>(* volitelná kapitola)</i>

            <h2 id="stud-formula">Studentovo rozdělení obecně</h2>
            <p>Studentovo rozdělení s {"$k$"} <strong>stupni volnosti</strong> má obecně hustotu danou vzorcem:
            {`$$
            \\begin{align*}
                f(x) &= \\frac{\\Gamma(\\frac{k+1}{2})}{\\sqrt{k}\\Gamma(\\frac{1}{2})\\Gamma(\\frac{k}{2})}\\left(1 + \\frac{x^2}{k}\\right)^{-\\frac{k+1}{2}}
            \\end{align*}
            $$`}
            </p>

            <h2 id="stud-motivation">Motivace</h2>
            <p>
            Když máme náhodný výběr {"$X_1, X_2, \\ldots, X_n$"} z normálního rozdělení {"$N(\\mu, \\sigma^2)$"},
            pak výběrový průměr {"$\\bar{X} = \\frac{1}{n}\\sum_{i=1}^{n} X_i$"} má normální rozdělení {"$N(\\mu, \\frac{\\sigma^2}{n})$"},
            a standardizovaný výběrový průměr
            {`$$
            \\begin{align*}
                Z = \\frac{\\bar{X} - \\mu}{\\sqrt{\\frac{\\sigma^2}{n}}}
            \\end{align*}
            $$`}
            má rozdělení {"$N(0, 1)$"}. Toto lze použít pro testování hypotéz o {"$\\mu$"} a pro intervalové odhady {"$\\mu$"}.
            </p>
            <p>
            {/* Na základě tohoto bychom mohli např. mohli udělat intervalový odhad pro {"$\\mu$"}:
            {`$$
            \\begin{align*}
                \\bar{X} - u_{\\alpha/2}\\frac{\\sigma}{\\sqrt{n}} < \\mu < \\bar{X} + u_{\\alpha/2}\\frac{\\sigma}{\\sqrt{n}}
            \\end{align*}
            $$`} */}
            Problém je, že neznáme rozptyl {"$\\sigma^2$"}.
            Místo {"$\\sigma^2$"} použijeme výběrový rozptyl {"$S^2 = \\frac{1}{n-1}\\sum_{i=1}^{n} (X_i - \\bar{X})^2$"}.
            Pak náhodná veličina
            {`$$
            \\begin{align*}
                T = \\frac{\\bar{X} - \\mu}{\\sqrt{\\frac{S^2}{n}}}
            \\end{align*}
            $$`}
            je mírně jiná náhodná veličina než standardizovaný výběrový průměr nahoře - a otázka je, jaké má rozdělení.
            Toto rozdělení lze odvodit klasickými metodami, a nazývá se <strong>Studentovo rozdělení</strong> (s {"$n-1$"} stupni volnosti).
            Viz např. <ELink href="https://en.wikipedia.org/wiki/Student%27s_t-distribution">Wikipedia</ELink>.
            </p>
            <p>Vzhledem k odvození níže je potřeba rozepsat následující:
            </p>
            <ul>
                <li>Náhodná veličina {"${\\color{BrickRed}{Z = \\frac{\\bar{X} - \\mu}{\\sqrt{\\frac{\\sigma^2}{n}}}}}$"} má rozdělení {"$N(0, 1)$"}</li>
                <li>Náhodná veličina {"${\\color{RoyalBlue}{Y = (n-1)\\frac{S^2}{\\sigma^2}}}$"} má rozdělení {"$\\chi^2({\\color{magenta}{n-1}})$"}</li>
                <li>Náhodné veličiny {"${\\color{BrickRed}{Z}}$"} a {"${\\color{RoyalBlue}{Y}}$"} jsou nezávislé.</li>
                <li>Náhodná veličina {"$T$"}:
                    {`$$\\begin{align*}
                        T = \\frac{{\\color{BrickRed}{Z}}}{\\sqrt{\\frac{{\\color{RoyalBlue}{Y}}}{{\\color{magenta}{n-1}}}}} = \\frac{ {\\color{BrickRed}{\\frac{\\bar{X} - \\mu}{ \\sqrt{ \\frac{\\sigma^2}{n}}}}}} {\\sqrt{\\frac{{\\color{RoyalBlue}{(n-1)\\frac{S^2}{\\sigma^2}}}}{{\\color{magenta}{n-1}}}}} = \\frac{{\\color{BrickRed}{\\bar{X} - \\mu}}}{\\sqrt{\\frac{{\\color{RoyalBlue}{S^2}}}{{\\color{BrickRed}{n}}}}}
                    \\end{align*}$$`}
                    {/*                                               A---------................|-------/-------X------------X/|A                     |------/-------X------------------------------X/| */}
                </li>
            </ul>

            <h2 id="stud-derivation">Odvození</h2>
            <p>
            Mějme {"$Z \\sim N(0, 1)$"} a {"$Y \\sim \\chi^2(k)$"} nezávislé náhodné veličiny.
            Pak náhodná veličina {"$T = \\frac{Z}{\\sqrt{\\frac{Y}{k}}}$"} má Studentovo rozdělení s {"$k$"} stupni volnosti.
            </p>
            <p>
            Sdružená hustota {"$f(z,y)$"} náhodného vektoru {"$(Z,\\sqrt{Y/k})$"} bude součinem hustot {"$f_Z(z)$"} a {"$f_\\sqrt{Y/k}(y)$"}.
            </p>
            <p>
            Nejdříve určíme hustotu {"$f_\\sqrt{Y/k}(y)$"}:
            {`$$
            \\begin{align*}
                F_{\\sqrt{Y/k}}(y) &= P(\\sqrt{Y/k} \\leq y) = P(Y \\leq ky^2) = \\int_0^{ky^2} f_Y(t)\\,\\text{d}t = \\\\
                &= \\int_0^{ky^2} \\frac{1}{2^{\\frac{k}{2}}\\Gamma(\\frac{k}{2})}t^{\\frac{k}{2}-1}e^{-\\frac{t}{2}}\\,\\text{d}t = \\\\
                &= \\big|\\text{sub: } t = ks^2 \\ \\ \\Rightarrow \\ \\ \\text{d}t = 2ks\\,\\text{d}s\\big| = \\\\
                &= \\int_0^{y} \\frac{1}{{\\color{BurntOrange}{2}}^{\\frac{k}{2}}\\Gamma(\\frac{k}{2})}({\\color{Fuchsia}{k}}{\\color{Turquoise}{s^2}})^{\\frac{k}{2}-1}e^{-\\frac{ks^2}{2}} {\\color{BurntOrange}{2}}{\\color{Fuchsia}{k}}{\\color{Turquoise}{s}}\\,\\text{d}s = \\\\
                &= \\int_0^{y} \\frac{{\\color{Fuchsia}{k}}^{\\frac{k}{2}}}{{\\color{BurntOrange}{2}}^{\\frac{k}{2}-1}\\Gamma(\\frac{k}{2})} {\\color{Turquoise}{s}}^{k-1}e^{-\\frac{ks^2}{2}}\\,\\text{d}s \\\\
                & \\\\
                f_{\\sqrt{Y/k}}(y) &= \\frac{k^{\\frac{k}{2}}}{2^{\\frac{k}{2}-1}\\Gamma(\\frac{k}{2})} y^{k-1}e^{-\\frac{ky^2}{2}} \\\\
            \\end{align*}
            $$`}
            </p>
            <p>Sdružená hustota {"$f(z,y)$"} je tedy:
            {`$$
            \\begin{align*}
                f(z,y) &= f_Z(z)f_{\\sqrt{Y/k}}(y) = \\\\
                &= \\frac{1}{\\sqrt{2\\pi}}e^{-\\frac{z^2}{2}} \\cdot \\frac{k^{\\frac{k}{2}}}{2^{\\frac{k}{2}-1}\\Gamma(\\frac{k}{2})} y^{k-1}e^{-\\frac{ky^2}{2}} \\\\
            \\end{align*}
            $$`}
            Provedeme transformaci souřadnic:
            {`$$
            \\begin{align*}
                \\begin{pmatrix}
                    u \\\\
                    v
                \\end{pmatrix} = \\begin{pmatrix}
                    \\frac{z}{y} \\\\
                    y
                \\end{pmatrix}
                \\Rightarrow
                \\begin{pmatrix}
                    z \\\\
                    y
                \\end{pmatrix} = \\begin{pmatrix}
                    uv \\\\
                    v
                \\end{pmatrix}
                \\Rightarrow
                \\begin{pmatrix}
                    \\frac{\\partial z}{\\partial u} & \\frac{\\partial z}{\\partial v} \\\\
                    \\frac{\\partial y}{\\partial u} & \\frac{\\partial y}{\\partial v}
                \\end{pmatrix} &= \\begin{pmatrix}
                    v & u \\\\
                    0 & 1
                \\end{pmatrix} \\\\
            \\end{align*}
            $$`}
            {`$$
            \\begin{align*}
            f(z,y)\\,\\text{d}z\\,\\text{d}y &= f\\big(uv,v\\big)v\\,\\text{d}u\\,\\text{d}v = \\\\
            &= \\frac{1}{\\sqrt{2\\pi}}e^{-\\frac{(uv)^2}{2}} \\cdot \\frac{k^\\frac{k}{2}}{2^{\\frac{k}{2}-1}\\Gamma(\\frac{k}{2})} v^{k-1}e^{-\\frac{kv^2}{2}} v\\,\\text{d}u\\,\\text{d}v
            \\end{align*}
            $$`}
            {`$$
            \\begin{align*}
            F(x) & = \\int\\int_{\\frac{z}{\\sqrt{y/k}} \\leq x} f(z,y)\\,\\text{d}z\\,\\text{d}y = \\int_{u = -\\infty}^{x} \\int_{v = 0}^{\\infty} \\frac{1}{\\sqrt{2\\pi}} \\frac{k^{\\frac{k}{2}}}{2^{\\frac{k}{2}-1}\\Gamma(\\frac{k}{2})} v^{k}e^{-\\frac{(uv)^2}{2}}e^{-\\frac{kv^2}{2}}\\,\\text{d}v\\,\\text{d}u = \\\\
                 & = \\int_{-\\infty}^{x} \\int_{0}^{\\infty} \\frac{1}{\\sqrt{\\pi}} \\frac{k^{\\frac{k}{2}}}{2^{\\frac{k}{2}-\\frac{1}{2}}\\Gamma(\\frac{k}{2})} v^k e^{-v^2\\frac{k+u^2}{2}}\\,\\text{d}v\\,\\text{d}u = \\\\
                 & = {\\Huge|}\\text{sub: } t = v^2\\frac{k+u^2}{2} \\ \\ \\Rightarrow \\ \\  v = \\sqrt{\\frac{2t}{k+u^2}} \\ \\ \\Rightarrow \\ \\  \\text{d}v = \\sqrt{\\frac{1}{2t(k+u^2)}} \\text{d}t {\\Huge|} = \\\\
                 & = {\\Huge|}\\text{helper: } v^k\\text{d}v = {\\color{magenta}{\\Big(\\frac{2t}{k+u^2} \\Big)^{\\frac{k}{2}} \\frac{(2t)^{-\\frac{1}{2}}}{(k+u^2)^{\\frac{1}{2}}}}} {\\Huge|} = \\\\
                 & = \\int_{-\\infty}^{x} \\int_{0}^{\\infty} \\frac{1}{\\sqrt{\\pi}} \\frac{k^{\\frac{k}{2}}}{2^{\\frac{k}{2}-\\frac{1}{2}}\\Gamma(\\frac{k}{2})} {\\color{magenta}{\\frac{2^{\\frac{k}{2} - \\frac{1}{2}} t^{\\frac{k}{2} - \\frac{1}{2}}}{(k+u^2)^{\\frac{k}{2} + \\frac{1}{2}}}}} e^{-t}\\,\\text{d}t\\,\\text{d}u = \\\\
                 & = \\int_{-\\infty}^{x} \\frac{1}{{\\color{Emerald}{\\sqrt{\\pi}}}} \\frac{{\\color{RoyalBlue}{k^{\\frac{k}{2}+\\frac{1}{2}}} }k^{-\\frac{1}{2}}}{\\Gamma(\\frac{k}{2})} \\frac{1}{(k+u^2)^{\\frac{k}{2} + \\frac{1}{2}}}  {\\huge[} \\int_{0}^{\\infty} t^{\\frac{k}{2} {\\color{red}{- \\frac{1}{2}}}} e^{-t}\\,\\text{d}t {\\huge]}\\,\\text{d}u = \\\\
                 & = \\int_{-\\infty}^{x} \\frac{1}{{\\color{Emerald}{\\Gamma(\\frac{1}{2})}}} \\frac{k^{-\\frac{1}{2}}}{\\Gamma(\\frac{k}{2})} \\frac{1}{\\frac{(k+u^2)^{\\frac{k}{2} + \\frac{1}{2}}}{{\\color{RoyalBlue}{k^{\\frac{k}{2}+\\frac{1}{2}}}}}}  {\\huge[} \\int_{0}^{\\infty} t^{\\frac{k}{2} {\\color{red}{+ \\frac{1}{2} - 1}}} e^{-t}\\,\\text{d}t{\\huge]}\\,\\text{d}u = \\\\
                 & = \\int_{-\\infty}^{x} \\frac{1}{\\Gamma(\\frac{1}{2})} \\frac{k^{-\\frac{1}{2}}}{\\Gamma(\\frac{k}{2})} \\frac{1}{(1+\\frac{u^2}{k})^{\\frac{k}{2} + \\frac{1}{2}}}  \\Gamma{\\Big(\\frac{k}{2}+\\frac{1}{2}\\Big)} \\,\\text{d}u = \\\\
                 & = \\int_{-\\infty}^{x} \\frac{1}{\\sqrt{k}}\\frac{\\Gamma{\\Big(\\frac{k}{2}+\\frac{1}{2}\\Big)}}{\\Gamma(\\frac{1}{2})\\Gamma(\\frac{k}{2})} \\Big(1+\\frac{u^2}{k}\\Big)^{-\\frac{k+1}{2}} \\,\\text{d}u \\\\
            \\end{align*}
            $$`}
            </p>

            <h2 id="xxx">Důkaz nezávislosti Z a Y</h2>
            <p>TODO
            {/* {`$$
            \\begin{align*}
                \\begin{pmatrix}
                    Z \\\\
                    Y
                \\end{pmatrix} = \\begin{pmatrix}
                    \\frac{\\bar{X} - \\mu}{\\sqrt{\\frac{S^2}{n}}} \\\\
                    (n-1)\\frac{S^2}{\\sigma^2}
                \\end{pmatrix}
                \\Rightarrow
                \\begin{pmatrix}
                    \\bar{X} \\\\
                    S^2
                \\end{pmatrix} = \\begin{pmatrix}
                    \\mu + \\sqrt{\\frac{S^2}{n}}Z \\\\
                    \\frac{\\sigma^2}{n-1}Y
                \\end{pmatrix}
                \\Rightarrow
                \\begin{pmatrix}
                    \\frac{\\partial Z}{\\partial \\bar{X}} & \\frac{\\partial Z}{\\partial S^2} \\\\
                    \\frac{\\partial Y}{\\partial \\bar{X}} & \\frac{\\partial Y}{\\partial S^2}
                \\end{pmatrix} &= \\begin{pmatrix}
                    \\frac{1}{\\sqrt{\\frac{S^2}{n}}} & -\\frac{\\bar{X} - \\mu}{2S^4}\\frac{\\partial S^2}{\\partial S^2} \\\\
                    0 & \\frac{(n-1)\\frac{\\partial S^2}{\\partial S^2}}{\\sigma^2}
                \\end{pmatrix} \\\\
                \\Rightarrow
                \\begin{pmatrix}
                    \\frac{\\partial Z}{\\partial \\bar{X}} & \\frac{\\partial Z}{\\partial S^2} \\\\
                    \\frac{\\partial Y}{\\partial \\bar{X}} & \\frac{\\partial Y}{\\partial S^2}
                \\end{pmatrix}
            \\end{align*}
            $$`} */}
            </p>
            <Footer/>
        </ChapterWrapper>
    );
}

